/**
 * NewMessageModal - Search users to start a direct message.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { searchUsers } from '../helpers/searchHelpers';
import { createConversation } from '../helpers/messagesHelpers';
import ModalShell from './ModalShell';
import UserAvatar from '../components/Profile/UserAvatar';
import toast from 'react-hot-toast';

const SEARCH_DEBOUNCE_MS = 300;

const UserSearchResults = React.memo(function UserSearchResults({
  query,
  results,
  searching,
  starting,
  currentUserId,
  onStartConversation,
}) {
  return (
    <div className="max-h-48 overflow-y-auto" data-testid="new-message-results" aria-busy={searching}>
      {searching && results.length === 0 ? (
        <p className="text-silver text-sm py-2">Searching...</p>
      ) : results.length === 0 ? (
        <p className="text-silver text-sm py-2">
          {query.trim() ? 'No users found' : 'Type to search users'}
        </p>
      ) : (
        <>
          {searching && (
            <p className="text-silver text-xs py-1" role="status">
              Updating results...
            </p>
          )}
          <ul className="space-y-1">
            {results.map((u) => {
              const isSelf = u.user_id === currentUserId;
              const disabled = isSelf || starting !== null;
              return (
                <li key={u.user_id}>
                  <button
                    type="button"
                    onClick={() => onStartConversation(u)}
                    disabled={disabled}
                    className="w-full flex items-center gap-3 p-2 rounded-lg text-left hover:bg-shadow disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <UserAvatar user={u} className="w-8 h-8 rounded-full" emojiClassName="text-sm" />
                    <span className="text-ash text-sm flex-1 truncate">
                      {u.displayname || u.username}
                      {isSelf && ' (you)'}
                    </span>
                    {!isSelf && (
                      <span className="text-ether text-xs font-medium">
                        {starting === u.user_id ? 'Starting...' : 'Message'}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
});

function NewMessageModal({ currentUserId, existingConversationIds, onClose, onStartConversation }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [starting, setStarting] = useState(null);

  const doSearch = useCallback(async (q) => {
    if (!q?.trim()) {
      setResults([]);
      return;
    }
    setSearching(true);
    try {
      const users = await searchUsers(q.trim());
      setResults(Array.isArray(users) ? users : []);
    } catch {
      setResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => doSearch(query), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query, doSearch]);

  const handleStartConversation = async (user) => {
    if (user.user_id === currentUserId) return;
    if (starting) return;
    setStarting(user.user_id);
    try {
      const conv = await createConversation(user.user_id);
      toast.success(`Started conversation with ${user.displayname || user.username}`);
      onClose?.();
      onStartConversation?.(conv);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setStarting(null);
    }
  };

  return (
    <ModalShell title="New message" onClose={onClose}>
      <div className="space-y-4">
        <div>
          <label htmlFor="new-message-search" className="block text-ash text-sm font-medium mb-1">
            Search users to message
          </label>
          <input
            id="new-message-search"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name or username..."
            className="w-full px-4 py-3 rounded-xl bg-shadow border border-charcoal text-ash placeholder-silver focus:outline-none focus:ring-2 focus:ring-ether/50"
            autoFocus
          />
        </div>
        <UserSearchResults
          query={query}
          results={results}
          searching={searching}
          starting={starting}
          currentUserId={currentUserId}
          onStartConversation={handleStartConversation}
        />
        <button
          type="button"
          onClick={onClose}
          className="w-full py-3 rounded-xl border border-shadow text-ash font-medium hover:bg-shadow"
        >
          Cancel
        </button>
      </div>
    </ModalShell>
  );
}

export default NewMessageModal;
